/**
 * storageQuota.js
 *
 * Quota-aware persistence layer on top of AsyncStorage.
 *
 * Every platform this app ships on has a hard ceiling on key-value storage, and
 * hitting it throws from `setItem` — which, on an unguarded `await`, aborts the
 * whole calling flow (login, background sync, day opening):
 *
 *  - **Web / Tauri webview** — AsyncStorage is backed by `window.localStorage`,
 *    ~5 MB TOTAL across all keys (Safari 5 MB, Chrome 5 MB, Firefox 10 MB).
 *    Overflow → `QuotaExceededError: Failed to execute 'setItem' on 'Storage':
 *    Setting the value of 'restaurant' exceeded the quota.` (the Sentry group
 *    this module exists for). With `webAsyncStorage.js` aliased in the web
 *    build the ceiling moves to IndexedDB's (hundreds of MB), but a browser in
 *    private mode / with storage blocked still falls back to localStorage, so
 *    the guard stays mandatory.
 *  - **Android** — AsyncStorage is one SQLite DB; the cap is
 *    `AsyncStorage_db_size_in_MB` (this repo sets 400 in `android/gradle.properties`).
 *    Overflow → `SQLiteFullException` / "database or disk is full". A device that
 *    is genuinely out of flash hits this at any DB size.
 *  - **iOS** — a manifest plus one file per large value; no hard cap, but a full
 *    disk fails the write the same way.
 *
 * The strategy, in order:
 *   1. **Don't write it twice.** Skip the write entirely when the serialized
 *      value is byte-identical to what is already stored. The background sync
 *      rewrites `restaurant` / `foods` / `admins` on every cycle and the payload
 *      is almost always unchanged — on a low-RAM device, skipping means no
 *      multi-MB string is handed to the storage engine (no SQLite page churn,
 *      no synchronous localStorage serialization, no GC spike).
 *   2. **Reclaim, then retry.** On a quota error, evict re-fetchable caches in a
 *      documented least-valuable-first order and try again once.
 *   3. **Degrade.** Let the caller supply progressively smaller projections of
 *      the same value (see `restaurantCache.persistRestaurant`).
 *   4. **Never throw.** A cache write failing must not take down the flow that
 *      triggered it. The caller learns it failed from the return value.
 *
 * @module helpers/storageQuota
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Keys that must survive every reclaim pass. Losing any of these logs the user
 * out, drops the active store scope, or discards writes that were never
 * accepted by the server — none of which is recoverable by a re-fetch.
 * @type {string[]}
 */
export const PROTECTED_KEYS = [
    'HG_USER_INFO',
    'HG_ACCESS_TOKEN',
    'HG_DEVICE_TRUST_TOKEN',
    'HG_DEVICE_TRUST_ID',
    'HG_LOGOUT_REASON',
    'restaurant',
    'restaurantPlace',
    'userAppMode',
    'lastUser',
];

/**
 * Key prefixes that must survive every reclaim pass — offline work that has not
 * reached the server yet.
 * @type {string[]}
 */
export const PROTECTED_PREFIXES = [
    'offlineQueue',
    'offline:',
    'pendingOrders',
    'unsynced',
];

/**
 * Eviction plan, least valuable first. Every entry is re-fetchable from the API
 * on the next sync, so dropping it costs a round trip, never data.
 *
 * `prefix` entries evict every key starting with that string (resolved through
 * `getAllKeys`); `key` entries evict one exact key.
 *
 * Tier rationale:
 *  - **1** mail bodies — biggest single consumer on web (5 × up to 192 KB) and
 *    re-downloaded the moment the message is opened again.
 *  - **2** per-sync scratch — overwritten wholesale by the next background pass.
 *  - **3** orders — re-pulled by `SyncManager`; costs a visible refresh.
 *  - **4** the POS catalog — last resort: dropping it disables offline selling
 *    until the next successful sync.
 *
 * @type {Array<{tier: number, key?: string, prefix?: string}>}
 */
export const RECLAIM_PLAN = [
    { tier: 1, prefix: '@mail_cache_msg:' },
    { tier: 1, key: '@mail_cache_msg_index' },
    { tier: 1, key: '@mail_cache_push_stub' },
    { tier: 2, key: 'lastOrderResponse' },
    { tier: 2, key: 'bookings' },
    { tier: 2, key: 'vipCards' },
    { tier: 2, key: 'admins' },
    { tier: 3, key: 'orders' },
    { tier: 4, key: 'foods' },
    { tier: 4, key: 'categories' },
];

/** Highest tier `reclaimStorage` will touch unless explicitly raised. @type {number} */
export const DEFAULT_MAX_RECLAIM_TIER = 3;

/**
 * Whether an error means "the storage backend is out of room".
 *
 * Covers all four backends this app runs on, because each reports it
 * differently and none of them share an error class:
 *  - browsers: `DOMException` named `QuotaExceededError` (code 22) or Firefox's
 *    `NS_ERROR_DOM_QUOTA_REACHED` (code 1014); the message is the only reliable
 *    signal in some WebViews ("exceeded the quota");
 *  - Android SQLite: `SQLiteFullException`, "database or disk is full";
 *  - iOS / Node: `ENOSPC`, "no space left on device";
 *  - IndexedDB (webAsyncStorage): `QuotaExceededError` on the transaction.
 *
 * @param {*} error Anything thrown by a storage call.
 * @returns {boolean} true when the write failed for lack of space.
 */
export const isQuotaError = (error) => {
    if (!error) {
        return false;
    }

    const name = String(error.name || '');
    const code = error.code;
    const message = String(error.message || error || '').toLowerCase();

    if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') {
        return true;
    }

    // DOMException codes — 22 is QUOTA_EXCEEDED_ERR, 1014 is Firefox's legacy code.
    if (code === 22 || code === 1014 || code === 'ENOSPC') {
        return true;
    }

    return (
        message.includes('exceeded the quota')
        || message.includes('quota_exceeded')
        || message.includes('quotaexceeded')
        || message.includes('exceeds the quota')
        || message.includes('database or disk is full')
        || message.includes('sqlitefullexception')
        || message.includes('disk is full')
        || message.includes('no space left')
        || message.includes('storage is full')
    );
};

/**
 * Approximate the on-disk cost of a string. Web localStorage stores UTF-16, so
 * a character is 2 bytes; SQLite/iOS store UTF-8 and are therefore cheaper —
 * this deliberately over-estimates so a "will it fit" check errs safe.
 *
 * @param {string|null|undefined} value Serialized value.
 * @returns {number} Approximate bytes.
 */
export const approximateSize = (value) => (typeof value === 'string' ? value.length * 2 : 0);

/**
 * Resolve the concrete key list for a reclaim entry.
 *
 * @param {{key?: string, prefix?: string}} entry One `RECLAIM_PLAN` row.
 * @param {string[]} allKeys Every key currently in storage.
 * @returns {string[]} Keys this entry would evict.
 */
const resolveEntryKeys = (entry, allKeys) => {
    if (entry.key) {
        return allKeys.includes(entry.key) ? [entry.key] : [];
    }

    if (entry.prefix) {
        return allKeys.filter((key) => key.startsWith(entry.prefix));
    }

    return [];
};

/**
 * Whether a key may never be evicted.
 *
 * @param {string} key Storage key.
 * @returns {boolean} true when the key is protected.
 */
export const isProtectedKey = (key) => PROTECTED_KEYS.includes(key)
    || PROTECTED_PREFIXES.some((prefix) => key.startsWith(prefix));

/**
 * Read every key currently in storage. `getAllKeys` is absent from some test
 * doubles and from very old AsyncStorage builds, so this degrades to the exact
 * keys named in the reclaim plan rather than failing the whole reclaim pass.
 *
 * @returns {Promise<string[]>} Known keys (possibly a subset).
 */
const listKeys = async () => {
    try {
        if (typeof AsyncStorage.getAllKeys === 'function') {
            const keys = await AsyncStorage.getAllKeys();

            if (Array.isArray(keys)) {
                return keys.map((key) => String(key));
            }
        }
    } catch (_error) {
        // Fall through to the static plan below.
    }

    return RECLAIM_PLAN.map((entry) => entry.key).filter(Boolean);
};

/**
 * Evict re-fetchable caches until `targetBytes` have been freed (or the plan is
 * exhausted), walking `RECLAIM_PLAN` least-valuable-first.
 *
 * Never throws: a reclaim pass is itself best-effort, and a storage backend that
 * fails to *delete* is already beyond what this layer can repair.
 *
 * @param {Object} [options] Reclaim options.
 * @param {number} [options.targetBytes=0] Stop once at least this many bytes are
 *   freed. `0` frees everything up to `maxTier`.
 * @param {number} [options.maxTier=DEFAULT_MAX_RECLAIM_TIER] Highest plan tier to
 *   touch. Raise to 4 to sacrifice the offline POS catalog.
 * @param {string[]} [options.exclude=[]] Extra keys to leave alone (e.g. the key
 *   currently being written).
 * @returns {Promise<{freedBytes: number, removedKeys: string[]}>} What was evicted.
 */
export const reclaimStorage = async ({
    targetBytes = 0,
    maxTier = DEFAULT_MAX_RECLAIM_TIER,
    exclude = [],
} = {}) => {
    const removedKeys = [];
    let freedBytes = 0;

    try {
        const allKeys = await listKeys();
        const skip = new Set(exclude);

        for (const entry of RECLAIM_PLAN) {
            if (entry.tier > maxTier) {
                break;
            }

            if (targetBytes > 0 && freedBytes >= targetBytes) {
                break;
            }

            const candidates = resolveEntryKeys(entry, allKeys)
                .filter((key) => !skip.has(key) && !isProtectedKey(key));

            if (!candidates.length) {
                continue;
            }

            // Measure before deleting so the caller can tell whether retrying is
            // even worth it — a reclaim that freed nothing will not save the write.
            for (const key of candidates) {
                try {
                    freedBytes += approximateSize(await AsyncStorage.getItem(key));
                } catch (_error) {
                    // An unreadable key still frees space when removed.
                }
            }

            try {
                if (typeof AsyncStorage.multiRemove === 'function') {
                    await AsyncStorage.multiRemove(candidates);
                } else {
                    for (const key of candidates) {
                        await AsyncStorage.removeItem(key);
                    }
                }
                removedKeys.push(...candidates);
            } catch (_error) {
                // Keep walking the plan — a later tier may still free enough.
            }
        }
    } catch (error) {
        console.warn('[storageQuota] reclaim pass failed (non-fatal):', error?.message || error);
    }

    return { freedBytes, removedKeys };
};

/**
 * Persist a value, surviving a full storage backend.
 *
 * Sequence: identical-value skip → write → (on quota error) reclaim → retry →
 * caller-supplied smaller projections, each reclaim-and-retried in turn. Any
 * non-quota error is reported and swallowed exactly the same way, because no
 * caller of a *cache* write has a better answer than "carry on without it".
 *
 * @param {string} key AsyncStorage key to write.
 * @param {string} value Serialized value (already a string — callers own their
 *   own `JSON.stringify` so a projection ladder can be built without re-parsing).
 * @param {Object} [options] Write options.
 * @param {Array<() => (string|null)>} [options.fallbacks=[]] Progressively smaller
 *   serializations of the same value, tried in order after a reclaim+retry fails.
 * @param {number} [options.maxTier=DEFAULT_MAX_RECLAIM_TIER] Highest reclaim tier.
 * @param {boolean} [options.skipIfUnchanged=true] Skip the write when the stored
 *   value is byte-identical. Set false for keys whose write is a side effect
 *   (e.g. a "last written at" marker).
 * @param {string} [options.label=key] Name used in log lines.
 * @returns {Promise<{ok: boolean, skipped: boolean, degraded: boolean, reclaimed: string[], error: (Error|null)}>}
 *   `ok` is true when storage now holds a value for `key` (written or already
 *   identical). `degraded` means a fallback projection was stored instead of the
 *   full value.
 */
export const safeSetItem = async (key, value, {
    fallbacks = [],
    maxTier = DEFAULT_MAX_RECLAIM_TIER,
    skipIfUnchanged = true,
    label = key,
} = {}) => {
    const outcome = { ok: false, skipped: false, degraded: false, reclaimed: [], error: null };

    if (typeof value !== 'string') {
        outcome.error = new TypeError(`safeSetItem expects a string value for "${key}"`);

        return outcome;
    }

    if (skipIfUnchanged) {
        try {
            if ((await AsyncStorage.getItem(key)) === value) {
                outcome.ok = true;
                outcome.skipped = true;

                return outcome;
            }
        } catch (_error) {
            // An unreadable current value just means we write unconditionally.
        }
    }

    // Candidate ladder: the full value first, then each caller-supplied
    // projection. `null`/non-string projections are skipped, so a caller can
    // return null to mean "nothing smaller to offer".
    const candidates = [value, ...fallbacks.map((build) => {
        try {
            return build();
        } catch (_error) {
            return null;
        }
    })].filter((candidate) => typeof candidate === 'string');

    for (let index = 0; index < candidates.length; index += 1) {
        const candidate = candidates[index];

        try {
            await AsyncStorage.setItem(key, candidate);
            outcome.ok = true;
            outcome.degraded = index > 0;

            if (outcome.degraded) {
                console.warn(`[storageQuota] "${label}" stored as a reduced projection (level ${index}) — storage is near capacity.`);
            }

            return outcome;
        } catch (error) {
            outcome.error = error;

            if (!isQuotaError(error)) {
                console.warn(`[storageQuota] could not persist "${label}": ${error?.message || error}`);

                return outcome;
            }

            // Reclaim once per candidate: a bigger candidate may need more room
            // than the previous pass freed, and each pass walks the plan again
            // over whatever is left.
            const { removedKeys } = await reclaimStorage({
                targetBytes: approximateSize(candidate),
                maxTier,
                exclude: [key],
            });
            outcome.reclaimed.push(...removedKeys);

            if (removedKeys.length) {
                try {
                    await AsyncStorage.setItem(key, candidate);
                    outcome.ok = true;
                    outcome.degraded = index > 0;
                    outcome.error = null;
                    console.warn(`[storageQuota] "${label}" stored after evicting ${removedKeys.length} cache key(s).`);

                    return outcome;
                } catch (retryError) {
                    outcome.error = retryError;
                }
            }
        }
    }

    // Nothing fit. Leave whatever is already stored in place: a stale-but-valid
    // `restaurant` keeps the app usable, whereas removing it strands the user on
    // a store-less screen. Callers that need freshness check `ok`.
    console.warn(`[storageQuota] "${label}" could not be persisted at any size — keeping the previous value. Last error: ${outcome.error?.message || outcome.error}`);

    return outcome;
};

/**
 * `safeSetItem` for values that are not strings yet. Serialization failures
 * (circular references, BigInt) are reported like any other write failure
 * instead of throwing into the caller.
 *
 * @param {string} key AsyncStorage key to write.
 * @param {*} value Value to `JSON.stringify` and persist.
 * @param {Object} [options] Same options as `safeSetItem`.
 * @returns {Promise<{ok: boolean, skipped: boolean, degraded: boolean, reclaimed: string[], error: (Error|null)}>}
 */
export const safeSetJson = async (key, value, options = {}) => {
    let serialized = null;

    try {
        serialized = JSON.stringify(value);
    } catch (error) {
        console.warn(`[storageQuota] could not serialize "${key}": ${error?.message || error}`);

        return { ok: false, skipped: false, degraded: false, reclaimed: [], error };
    }

    if (typeof serialized !== 'string') {
        // `JSON.stringify(undefined)` is undefined — store JSON null so readers
        // get a parseable value instead of a torn key.
        serialized = 'null';
    }

    return safeSetItem(key, serialized, options);
};

export default safeSetItem;
